/** Dataverse record sharing only, not effective Copilot Studio access. */
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import {
  ApiError,
  ConfigurationError,
  PowerAutomateError,
  ValidationError,
} from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

const MAX_PRINCIPALS = 200;
const MAX_SERVICE_PRINCIPALS = 5000;
const RIGHTS = {
  None: 0,
  ReadAccess: 1,
  WriteAccess: 2,
  AppendAccess: 4,
  AppendToAccess: 16,
  CreateAccess: 32,
  DeleteAccess: 65536,
  ShareAccess: 262144,
  AssignAccess: 524288,
} as const;
type AccessRight = keyof typeof RIGHTS;
type PrincipalType = "systemuser" | "team" | "organization";
interface DirectPrincipal {
  type: PrincipalType;
  id: string;
  rights: AccessRight[];
}

export const getCopilotAgentPermissionsInputSchema = z
  .object({
    agentId: z.string().regex(GUID_PATTERN),
    componentId: z.string().regex(GUID_PATTERN).optional(),
    maxPrincipals: z.number().int().min(1).max(MAX_PRINCIPALS).default(MAX_PRINCIPALS),
  })
  .strict();

export const getCopilotAgentPermissionsTool = {
  name: "get_copilot_agent_permissions",
  description:
    "Read the current owner ID and Dataverse record-sharing principal IDs/rights for one Copilot Studio agent or a component verified to belong to it. Bounded to 200 principals with explicit truncation. This is not effective access: it does not resolve security roles, team membership, Studio coauthor/viewer access, channel/runtime access, or direct versus cascaded sharing origin. Never changes sharing or permissions and returns no principal names or emails.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "Existing agent GUID in the current environment",
      },
      componentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description:
          "Optional component GUID; its agent membership is verified before reading sharing",
      },
      maxPrincipals: {
        type: "integer",
        minimum: 1,
        maximum: MAX_PRINCIPALS,
        default: MAX_PRINCIPALS,
      },
    },
    required: ["agentId"],
    additionalProperties: false,
  },
  annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true },
};

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function id(value: unknown): string | undefined {
  return typeof value === "string" && GUID_PATTERN.test(value) ? value.toLowerCase() : undefined;
}

function invalidSharing(): never {
  throw new ApiError(
    "Dataverse record-sharing response is malformed or ambiguous; no permission verdict returned",
    502,
  );
}

async function safeRead<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    const status =
      error instanceof PowerAutomateError &&
      Number.isInteger(error.statusCode) &&
      error.statusCode !== undefined &&
      error.statusCode >= 400 &&
      error.statusCode <= 599
        ? error.statusCode
        : 502;
    // Do not return row names, principal attributes, raw API bodies, or credentials
    // from upstream errors. A failed read is never an empty sharing verdict.
    throw new ApiError(
      `Copilot Dataverse record-sharing read failed (HTTP ${status}); no permission verdict returned`,
      status,
    );
  }
}

function accessRights(value: unknown): AccessRight[] {
  let names: string[];
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) {
    const knownMask = Object.values(RIGHTS).reduce<number>((mask, right) => mask | right, 0);
    if (value > knownMask || (value & ~knownMask) !== 0) return invalidSharing();
    names =
      value === 0
        ? ["None"]
        : Object.entries(RIGHTS)
            .filter(([, mask]) => mask !== 0 && (value & mask) === mask)
            .map(([name]) => name);
  } else if (typeof value === "string" && value.length <= 256) {
    names = value.split(",").map((name) => name.trim());
  } else return invalidSharing();
  if (
    names.length === 0 ||
    new Set(names).size !== names.length ||
    names.some((name) => !Object.hasOwn(RIGHTS, name)) ||
    (names.includes("None") && names.length !== 1)
  )
    return invalidSharing();
  // Canonical order permits duplicate-record comparison without combining rights.
  return (Object.keys(RIGHTS) as AccessRight[]).filter((name) => names.includes(name));
}

function principal(value: unknown): DirectPrincipal {
  const item = record(value);
  const entity = record(item?.Principal);
  const rawType = entity?.["@odata.type"];
  const type =
    rawType === "#Microsoft.Dynamics.CRM.systemuser"
      ? "systemuser"
      : rawType === "#Microsoft.Dynamics.CRM.team"
        ? "team"
        : rawType === "#Microsoft.Dynamics.CRM.organization"
          ? "organization"
          : undefined;
  const typedId = type ? entity?.[`${type}id`] : undefined;
  const sharedOwnerId = entity?.ownerid;
  const principalId = id(typedId ?? sharedOwnerId);
  if (!type || !principalId) return invalidSharing();
  // Microsoft's documented response uses ownerid for polymorphic principals.
  // If both it and a type-specific primary key exist, they must agree.
  if (
    (typedId !== undefined && id(typedId) !== principalId) ||
    (sharedOwnerId !== undefined && id(sharedOwnerId) !== principalId)
  )
    return invalidSharing();
  for (const other of ["systemuser", "team", "organization"]) {
    if (other !== type && entity?.[`${other}id`] !== undefined) return invalidSharing();
  }
  return { type, id: principalId, rights: accessRights(item?.AccessMask) };
}

export async function handleGetCopilotAgentPermissions(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotAgentPermissionsInputSchema.parse(input);
  if (!context.api)
    throw new ConfigurationError("Dataverse is not configured for the current environment");
  const api = context.api;
  const agentId = parsed.agentId.toLowerCase();
  const agent = record(await safeRead(() => api.getCopilotSharingTarget("bots", agentId)));
  if (!agent || id(agent.botid) !== agentId)
    throw new ValidationError(
      "Returned agent identity could not be verified; sharing was not read",
    );
  const entitySet = parsed.componentId ? "botcomponents" : "bots";
  const targetId = parsed.componentId?.toLowerCase() ?? agentId;
  const target = parsed.componentId
    ? record(await safeRead(() => api.getCopilotSharingTarget("botcomponents", targetId)))
    : agent;
  if (
    !target ||
    (parsed.componentId &&
      (id(target.botcomponentid) !== targetId || id(target._parentbotid_value) !== agentId))
  ) {
    throw new ValidationError(
      "Returned component identity or parent agent could not be verified; sharing was not read",
    );
  }
  const ownerId = id(target._ownerid_value);
  if (!ownerId)
    throw new ValidationError("Current row owner ID could not be verified; sharing was not read");
  const ownerType = target["_ownerid_value@Microsoft.Dynamics.CRM.lookuplogicalname"];
  const payload = record(
    await safeRead(() => api.retrieveCopilotSharedPrincipalsAndAccess(entitySet, targetId)),
  );
  const values = payload?.PrincipalAccesses;
  if (!Array.isArray(values) || values.length > MAX_SERVICE_PRINCIPALS) return invalidSharing();
  // This function's documented response is not paged. Never follow injected URLs
  // or describe an unknown continuation as a complete list.
  if (Object.keys(payload ?? {}).some((key) => /(?:nextlink|deltalink)$/i.test(key))) {
    throw new ApiError(
      "Dataverse record-sharing returned an unsupported continuation; no complete sharing result returned",
      502,
    );
  }
  const unique = new Map<string, DirectPrincipal>();
  for (const value of values) {
    const entry = principal(value);
    const key = `${entry.type}:${entry.id}`;
    const previous = unique.get(key);
    if (previous && previous.rights.join(",") !== entry.rights.join(",")) return invalidSharing();
    unique.set(key, entry);
  }
  const principals = [...unique.values()];
  return JSON.stringify(
    {
      scope: "dataverse_record_sharing_only",
      agentId,
      target: { entitySet, id: targetId },
      owner: {
        id: ownerId,
        type: ownerType === "systemuser" || ownerType === "team" ? ownerType : "unknown",
      },
      principals: principals.slice(0, parsed.maxPrincipals),
      returned: Math.min(principals.length, parsed.maxPrincipals),
      reportedEntries: values.length,
      uniquePrincipals: principals.length,
      duplicateEntries: values.length - principals.length,
      truncated: principals.length > parsed.maxPrincipals,
      limitations:
        "Record-sharing results only; direct versus cascaded sharing origin is not distinguished. No security-role, team-membership, inherited-rights, effective Studio coauthor/viewer, or channel/runtime access analysis. No principals returned does not mean nobody can access the agent.",
    },
    null,
    2,
  );
}
